import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { CrudTable, type CrudColumn } from '@/shared/components/ui/crud-table';
import { useIsAdminTier } from '@/shared/auth/SessionProvider';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  createEfSubitem,
  listAllEfSubitems,
  softDeleteEfSubitem,
  updateEfSubitem,
  type EfSubitemItem,
} from '@/shared/api/controllers/efSubitems';
import { listActiveEfItems } from '@/shared/api/controllers/efItems';
import { invalidateAbmRelatedQueries } from '@/shared/api/abmCacheInvalidation';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import type { EntityViewProps } from '../../types';

const QUERY_KEY = ['configuracion', 'ef-subitems'] as const;

type Mode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; item: EfSubitemItem }
  | { kind: 'delete'; item: EfSubitemItem };

const schema = z.object({
  nombre: z.string().trim().min(1, 'Ingresá el EF SubItem'),
  efItemId: z.string().trim().min(1, 'Seleccioná el EF Item'),
  costoDirecto: z.boolean(),
  costoIndirecto: z.boolean(),
});
type Values = z.infer<typeof schema>;

const empty: Values = { nombre: '', efItemId: '', costoDirecto: false, costoIndirecto: false };

function itemToForm(item: EfSubitemItem): Values {
  return {
    nombre: item.fields.Nombre ?? '',
    efItemId: item.fields.EfItemId != null ? String(item.fields.EfItemId) : '',
    costoDirecto: Boolean(item.fields.CostoDirecto),
    costoIndirecto: Boolean(item.fields.CostoIndirecto),
  };
}

export function EfSubitemsEntity({
  search,
  filterValue,
  onStatus,
  openCreate,
  onCreateHandled,
}: EntityViewProps) {
  const queryClient = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const scopedKey = useMemo(
    () => [...QUERY_KEY, activeCountry.code] as readonly unknown[],
    [activeCountry.code],
  );
  const { data: rows = [], isPending, isFetching, error, refetch } = useQuery({
    queryKey: scopedKey,
    queryFn: listAllEfSubitems,
    staleTime: 60_000,
  });
  const [mode, setMode] = useState<Mode>({ kind: 'closed' });

  useEffect(() => {
    if (openCreate) {
      setMode({ kind: 'create' });
      onCreateHandled();
    }
  }, [openCreate, onCreateHandled]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let sorted = [...rows].sort((a, b) =>
      (a.fields.Nombre ?? '').localeCompare(b.fields.Nombre ?? '', 'es'),
    );
    if (filterValue) sorted = sorted.filter((r) => r.fields.Status === filterValue);
    if (!q) return sorted;
    return sorted.filter((r) => {
      const nombre = (r.fields.Nombre ?? '').toLowerCase();
      const padre = (r.fields.EfItemNombre ?? '').toLowerCase();
      return nombre.includes(q) || padre.includes(q);
    });
  }, [rows, search, filterValue]);

  const columns: CrudColumn<EfSubitemItem>[] = [
    { header: 'Status', width: 'w-32', render: (r) => <StatusBadge status={r.fields.Status} /> },
    {
      header: 'EF SubItem',
      render: (r) => <span className="font-medium text-foreground">{r.fields.Nombre || '—'}</span>,
    },
    {
      header: 'EF Item',
      render: (r) => <span className="text-muted-foreground">{r.fields.EfItemNombre || '—'}</span>,
    },
    { header: 'Costo Directo', render: (r) => (r.fields.CostoDirecto ? 'Sí' : 'No') },
    { header: 'Costo Indirecto', render: (r) => (r.fields.CostoIndirecto ? 'Sí' : 'No') },
  ];

  const closeDialog = () => setMode({ kind: 'closed' });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    invalidateAbmRelatedQueries(queryClient, 'ef-subitems');
  };

  const canEdit = useIsAdminTier();
  return (
    <>
      <CrudTable
        hideActions={!canEdit}
        rows={filtered}
        columns={columns}
        loading={isPending}
        error={error instanceof Error ? error : null}
        onRetry={() => refetch()}
        emptyMessage={search ? `No hay resultados para "${search}".` : 'Sin EF SubItems.'}
        onEdit={(r) => setMode({ kind: 'edit', item: r })}
        onDelete={(r) => setMode({ kind: 'delete', item: r })}
        canEdit={(r) => r.fields.Status !== 'Inactivo'}
        editDisabledTitle={() => 'El registro está inactivo'}
        canDelete={(r) => r.fields.Status !== 'Inactivo'}
        deleteDisabledTitle={() => 'El registro ya está inactivo'}
        isBackgroundFetching={isFetching}
      />

      {(mode.kind === 'create' || mode.kind === 'edit') && (
        <EfSubitemDialog
          open
          onOpenChange={(o) => !o && closeDialog()}
          item={mode.kind === 'edit' ? mode.item : null}
          existingRows={rows}
          onSuccess={(msg) => {
            invalidate();
            closeDialog();
            onStatus({ kind: 'success', message: msg });
          }}
          onError={(msg) => {
            closeDialog();
            onStatus({ kind: 'error', message: msg });
          }}
        />
      )}

      {mode.kind === 'delete' && (
        <DeleteEfSubitemDialog
          open
          onOpenChange={(o) => !o && closeDialog()}
          item={mode.item}
          onSuccess={(msg) => {
            invalidate();
            closeDialog();
            onStatus({ kind: 'success', message: msg });
          }}
          onError={(msg) => {
            closeDialog();
            onStatus({ kind: 'error', message: msg });
          }}
        />
      )}
    </>
  );
}

function EfSubitemDialog({
  open,
  onOpenChange,
  item,
  existingRows,
  onSuccess,
  onError,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: EfSubitemItem | null;
  existingRows: EfSubitemItem[];
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}) {
  const isEdit = Boolean(item);
  const { config: activeCountry } = useActiveCountry();
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: item ? itemToForm(item) : empty,
  });

  useEffect(() => {
    if (open) form.reset(item ? itemToForm(item) : empty);
  }, [open, item, form]);

  const { data: efItems = [] } = useQuery({
    queryKey: ['configuracion', 'ef-items', 'active', activeCountry.code],
    queryFn: listActiveEfItems,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: async (values: Values) => {
      const payload = {
        nombre: values.nombre,
        efItemId: Number(values.efItemId),
        costoDirecto: values.costoDirecto,
        costoIndirecto: values.costoIndirecto,
      };
      if (isEdit && item) return updateEfSubitem(item.id, payload);
      return createEfSubitem(payload);
    },
    onSuccess: () =>
      onSuccess(
        isEdit ? 'EF SubItem actualizado correctamente.' : 'EF SubItem creado correctamente.',
      ),
    onError: (err) =>
      onError(err instanceof Error ? err.message : 'No se pudo guardar el EF SubItem.'),
  });

  const onSubmit = form.handleSubmit((values) => {
    // Duplicate name within the same parent EF Item.
    const normalized = values.nombre.trim().toLowerCase();
    const duplicated = existingRows.find(
      (r) =>
        r.id !== item?.id &&
        String(r.fields.EfItemId ?? '') === values.efItemId &&
        (r.fields.Nombre ?? '').trim().toLowerCase() === normalized,
    );
    if (duplicated) {
      form.setError('nombre', {
        type: 'manual',
        message: 'Ya existe un EF SubItem con ese nombre en este EF Item.',
      });
      return;
    }
    mutation.mutate(values);
  });

  const efItemId = form.watch('efItemId');
  const costoDirecto = form.watch('costoDirecto');
  const costoIndirecto = form.watch('costoIndirecto');
  const watchedNombre = form.watch('nombre');

  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (!watchedNombre?.trim()) missing.push('EF SubItem');
    if (!efItemId) missing.push('EF Item');
    return missing;
  }, [watchedNombre, efItemId]);
  const canSubmit = missingFields.length === 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-lg"
        processing={mutation.isPending && (isEdit ? 'Guardando cambios…' : 'Creando…')}
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar EF SubItem' : 'Nuevo EF SubItem'}</DialogTitle>
          <DialogDescription>
            Asociá el EF SubItem a un EF Item y definí su tipo de costo.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden" noValidate>
          <DialogBody className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="nombre">
                EF SubItem <span className="text-primary">*</span>
              </Label>
              <Input
                id="nombre"
                variant="filled"
                placeholder="Ingresar EF SubItem"
                autoFocus
                {...form.register('nombre')}
              />
              {form.formState.errors.nombre && (
                <p className="text-xs font-medium text-destructive">
                  {form.formState.errors.nombre.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>
                EF Item <span className="text-primary">*</span>
              </Label>
              <Select
                value={efItemId ?? ''}
                onValueChange={(v) => form.setValue('efItemId', v, { shouldDirty: true })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar EF Item" />
                </SelectTrigger>
                <SelectContent>
                  {efItems.map((i) => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.fields.Nombre || '—'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <ToggleRow
                label="Costo Directo"
                description="El subítem es un costo directo."
                checked={costoDirecto}
                onChange={(v) => form.setValue('costoDirecto', v, { shouldDirty: true })}
              />
              <ToggleRow
                label="Costo Indirecto"
                description="El subítem es un costo indirecto."
                checked={costoIndirecto}
                onChange={(v) => form.setValue('costoIndirecto', v, { shouldDirty: true })}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            {missingFields.length > 0 && (
              <p className="mr-auto text-xs text-muted-foreground">
                Faltan completar:{' '}
                <span className="font-medium text-destructive">{missingFields.join(', ')}</span>
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !canSubmit}>
              {isEdit ? 'Guardar cambios' : 'Añadir'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-lg border bg-secondary/40 p-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 accent-primary"
      />
      <span className="flex flex-col">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </span>
    </label>
  );
}

function DeleteEfSubitemDialog({
  open,
  onOpenChange,
  item,
  onSuccess,
  onError,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: EfSubitemItem;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}) {
  const mutation = useMutation({
    mutationFn: async () => softDeleteEfSubitem(item.id),
    onSuccess: () => onSuccess('EF SubItem eliminado correctamente.'),
    onError: (err) =>
      onError(err instanceof Error ? err.message : 'No se pudo eliminar el EF SubItem.'),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md" processing={mutation.isPending && 'Eliminando…'}>
        <DialogHeader>
          <DialogTitle>Eliminar EF SubItem</DialogTitle>
          <DialogDescription>
            ¿Confirmás la eliminación de{' '}
            <strong className="text-foreground">{item.fields.Nombre || '—'}</strong>?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            Eliminar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
